import { Spinner } from '@/components/glitch-ui/spinner';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SectionHeader } from '../../glitch-ui/section-header';
import { useQuery } from '@tanstack/react-query';
import {
  userNotificationSettingsQueryOptions,
  useUpdateUserNotificationSettingsMutation,
} from '@/lib/query-options';
import { Mail, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/useToast';

interface NotificationSettingsProps {
  onSettingsChange?: (settings: Record<string, boolean>) => void;
}

export const NotificationSettings = ({ onSettingsChange }: NotificationSettingsProps) => {
  const { toast } = useToast();

  const {
    data: settings,
    isLoading,
    isError,
  } = useQuery(userNotificationSettingsQueryOptions());

  const updateMutation = useUpdateUserNotificationSettingsMutation();

  const handleToggle = async (enabled: boolean) => {
    try {
      await updateMutation.mutateAsync({ email_notifications_enabled: enabled });
      toast({
        title: enabled ? 'Email notifications enabled' : 'Email notifications disabled',
        description: enabled
          ? 'You will receive email notifications for platform alerts.'
          : 'You will no longer receive email notifications.',
      });
      onSettingsChange?.({ email_notifications_enabled: enabled });
    } catch {
      toast({
        title: 'Failed to update settings',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <SectionHeader title="Email Notifications" />
        <Card className="w-full">
          <CardContent className="p-6 flex items-center justify-center">
            <Spinner size="lg" className="text-muted-foreground" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <SectionHeader title="Email Notifications" />
        <Card className="w-full border-destructive/50">
          <CardContent className="p-6 flex items-center gap-3 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <span>Failed to load notification settings</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionHeader title="Email Notifications" />
      <Card className="w-full">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100">
              <Mail className="h-5 w-5 text-indigo-600" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base">Notification Emails</CardTitle>
              <CardDescription className="text-sm">
                Receive email alerts for platform notifications
              </CardDescription>
            </div>
            <Switch
              checked={settings?.email_notifications_enabled ?? false}
              onCheckedChange={handleToggle}
              disabled={updateMutation.isPending}
              className="data-[state=checked]:bg-[#290BD9]"
            />
          </div>
        </CardHeader>
        <CardContent className="pt-0 pb-6 px-6">
          <p className="text-sm text-muted-foreground">
            When enabled, you'll receive an email each time a new notification is generated 
            by the platform (e.g., optimization recommendations, campaign updates, system alerts).
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
